import { useState, useEffect, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { campaignAPI, mailingListAPI, emailTemplateAPI } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Send, Trash2, Eye, Users, Mail, CheckCircle, FileText } from "lucide-react";

const STATUS_MAP = {
  draft: { label: "Brouillon", class: "bg-slate-100 text-slate-600" },
  sending: { label: "En cours", class: "bg-blue-100 text-blue-700" },
  sent: { label: "Envoyé", class: "bg-emerald-100 text-emerald-700" },
  partial: { label: "Partiel", class: "bg-amber-100 text-amber-700" },
  error: { label: "Erreur", class: "bg-red-100 text-red-700" },
};

const VAR_HELP = [
  { var: "{client_name}", desc: "Nom du contact" },
  { var: "{company}", desc: "Société du client" },
  { var: "{company_name}", desc: "Nom de votre société" },
];

export default function CampaignsPage() {
  const [campaigns, setCampaigns] = useState([]);
  const [lists, setLists] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [form, setForm] = useState({ name: "", subject: "", message: "", list_ids: [], filter_type: "", filter_search: "" });
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    campaignAPI.list().then(r => setCampaigns(r.data.items || [])).catch(() => {});
    mailingListAPI.list().then(r => setLists(r.data.items || [])).catch(() => {});
    emailTemplateAPI.list().then(r => setTemplates(r.data.items || [])).catch(() => {});
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggleList = (listId) => {
    setForm(prev => {
      const ids = prev.list_ids.includes(listId) ? prev.list_ids.filter(id => id !== listId) : [...prev.list_ids, listId];
      return { ...prev, list_ids: ids };
    });
  };

  const openCreate = () => {
    setForm({ name: "", subject: "", message: "", list_ids: [], filter_type: "", filter_search: "" });
    setShowCreate(true);
  };

  const applyTemplate = (tplId) => {
    const tpl = templates.find(t => t.id === tplId);
    if (!tpl) return;
    setForm(prev => ({ ...prev, subject: tpl.subject, message: tpl.body }));
    toast.success(`Template "${tpl.name}" applique`);
  };

  const handleCreate = async () => {
    if (!form.subject || !form.message) { toast.error("Objet et message requis"); return; }
    if (form.list_ids.length === 0 && !form.filter_type) { toast.error("Sélectionnez au moins une liste ou un filtre"); return; }
    try { await campaignAPI.create(form); toast.success("Campagne créée"); setShowCreate(false); load(); } catch { toast.error("Erreur"); }
  };

  const handleCreateAndSend = async () => {
    if (!form.subject || !form.message) { toast.error("Objet et message requis"); return; }
    if (form.list_ids.length === 0 && !form.filter_type) { toast.error("Sélectionnez au moins une liste ou un filtre"); return; }
    setSending(true);
    try {
      const r = await campaignAPI.create(form);
      const sr = await campaignAPI.send(r.data.id);
      toast.success(sr.data.message || "Envoi en cours");
      setShowCreate(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur envoi"); }
    setSending(false);
  };

  const handleSend = async (id) => {
    setSending(true);
    try { const r = await campaignAPI.send(id); toast.success(r.data.message || "Envoi en cours"); load(); } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
    setSending(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Supprimer cette campagne ?")) return;
    try { await campaignAPI.delete(id); toast.success("Supprimee"); load(); } catch { toast.error("Erreur"); }
  };

  const getListNames = (listIds) => {
    if (!listIds?.length) return "-";
    return listIds.map(id => lists.find(l => l.id === id)?.name || id).join(", ");
  };

  const totalContactsInLists = form.list_ids.reduce((sum, id) => {
    const l = lists.find(ll => ll.id === id);
    return sum + (l?.contact_ids?.length || 0);
  }, 0);

  return (
    <div className="p-4 md:p-6 max-w-6xl mx-auto space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-800" data-testid="campaigns-title">Campagnes emails</h1>
          <p className="text-sm text-slate-500 mt-1">{campaigns.length} campagne(s)</p>
        </div>
        <Button size="sm" onClick={openCreate} data-testid="new-campaign-btn"><Plus size={14} className="mr-1.5" />Nouvelle campagne</Button>
      </div>

      <div className="border rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead>Campagne</TableHead>
              <TableHead>Listes</TableHead>
              <TableHead className="text-center">Dest.</TableHead>
              <TableHead className="text-center">Envoyes</TableHead>
              <TableHead className="text-center">Statut</TableHead>
              <TableHead className="text-center">Date</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {campaigns.length === 0 && <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-400">Aucune campagne</TableCell></TableRow>}
            {campaigns.map(c => {
              const st = STATUS_MAP[c.status] || STATUS_MAP.draft;
              return (
                <TableRow key={c.id} data-testid={`campaign-row-${c.id}`}>
                  <TableCell>
                    <div className="font-medium text-sm">{c.name || "Sans nom"}</div>
                    <div className="text-xs text-slate-400 truncate max-w-[180px]">{c.subject}</div>
                  </TableCell>
                  <TableCell className="text-xs text-slate-500 max-w-[150px] truncate">{getListNames(c.list_ids)}</TableCell>
                  <TableCell className="text-center text-sm">{c.total_recipients || "-"}</TableCell>
                  <TableCell className="text-center text-sm">{c.sent_count || 0}{c.error_count > 0 && <span className="text-red-500 ml-1">({c.error_count})</span>}</TableCell>
                  <TableCell className="text-center"><Badge className={`text-[10px] ${st.class}`}>{st.label}</Badge></TableCell>
                  <TableCell className="text-center text-xs text-slate-500">{c.sent_at ? new Date(c.sent_at).toLocaleDateString("fr-FR") : "-"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1">
                      <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => campaignAPI.get(c.id).then(r => setShowDetail(r.data))} data-testid={`view-campaign-${c.id}`}><Eye size={13} /></Button>
                      {c.status === "draft" && <Button variant="ghost" size="icon" className="h-7 w-7 text-indigo-600" onClick={() => handleSend(c.id)} disabled={sending} data-testid={`send-campaign-${c.id}`}><Send size={13} /></Button>}
                      <Button variant="ghost" size="icon" className="h-7 w-7 text-red-500" onClick={() => handleDelete(c.id)} data-testid={`delete-campaign-${c.id}`}><Trash2 size={13} /></Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {/* Create campaign dialog */}
      <Dialog open={showCreate} onOpenChange={v => { if (!v) setShowCreate(false); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Nouvelle campagne</DialogTitle></DialogHeader>
          <div className="space-y-4">
            {/* Template selector */}
            {templates.length > 0 && (
              <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3">
                <Label className="text-sm font-medium flex items-center gap-1.5 mb-2"><FileText size={14} />Utiliser un template</Label>
                <div className="flex flex-wrap gap-2">
                  {templates.map(t => (
                    <button key={t.id} type="button" onClick={() => applyTemplate(t.id)} className="text-xs px-3 py-1.5 bg-white border border-indigo-200 rounded-md hover:bg-indigo-100 hover:border-indigo-400 transition font-medium text-indigo-700" data-testid={`apply-template-${t.id}`}>{t.name}</button>
                  ))}
                </div>
              </div>
            )}

            <div><Label>Nom de la campagne</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Ex: Newsletter Mars 2026" data-testid="campaign-name" /></div>
            <div><Label>Objet du mail *</Label><Input value={form.subject} onChange={e => setForm({ ...form, subject: e.target.value })} placeholder="Ex: Nos nouveautes du mois" data-testid="campaign-subject" /></div>
            <div>
              <Label>Message *</Label>
              <Textarea rows={8} value={form.message} onChange={e => setForm({ ...form, message: e.target.value })} placeholder={"Bonjour {client_name},\n\n..."} data-testid="campaign-message" />
              <div className="flex flex-wrap gap-2 mt-2">
                {VAR_HELP.map(v => (
                  <button key={v.var} type="button" className="text-[11px] px-2 py-0.5 bg-slate-100 text-slate-600 rounded hover:bg-indigo-50 hover:text-indigo-600 transition" onClick={() => setForm({ ...form, message: form.message + v.var })}>{v.var} <span className="text-slate-400">({v.desc})</span></button>
                ))}
              </div>
            </div>

            {/* Destination: lists */}
            <div className="border-t pt-4">
              <Label className="text-base font-semibold flex items-center gap-2"><Users size={16} />Destinataires</Label>
              {lists.length > 0 ? (
                <div className="mt-3 space-y-2">
                  <p className="text-xs text-slate-500 mb-1">Sélectionnez une ou plusieurs listes :</p>
                  {lists.map(l => (
                    <label key={l.id} className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition ${form.list_ids.includes(l.id) ? "border-indigo-300 bg-indigo-50" : "border-slate-200 hover:bg-slate-50"}`} data-testid={`campaign-list-${l.id}`}>
                      <Checkbox checked={form.list_ids.includes(l.id)} onCheckedChange={() => toggleList(l.id)} />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium">{l.name}</p>
                        {l.description && <p className="text-xs text-slate-400 truncate">{l.description}</p>}
                      </div>
                      <Badge variant="outline" className="text-xs shrink-0">{(l.contact_ids || []).length} contacts</Badge>
                    </label>
                  ))}
                  {form.list_ids.length > 0 && (
                    <p className="text-sm text-indigo-600 font-medium mt-2" data-testid="campaign-list-total"><CheckCircle size={14} className="inline mr-1" />{totalContactsInLists} contact(s) dans {form.list_ids.length} liste(s)</p>
                  )}
                </div>
              ) : (
                <div className="mt-3 p-4 bg-slate-50 rounded-lg text-center">
                  <p className="text-sm text-slate-500">Aucune liste de diffusion.</p>
                  <p className="text-xs text-slate-400 mt-1">Creez d'abord une liste dans la section "Listes de diffusion".</p>
                </div>
              )}

              <details className="mt-3">
                <summary className="text-xs text-slate-400 cursor-pointer hover:text-slate-600">Ou utiliser un filtre rapide (sans liste)...</summary>
                <div className="flex gap-3 items-end mt-2">
                  <div className="flex-1">
                    <Label className="text-xs">Type</Label>
                    <Select value={form.filter_type || "none"} onValueChange={v => setForm({ ...form, filter_type: v === "none" ? "" : v })}>
                      <SelectTrigger data-testid="campaign-filter-type"><SelectValue /></SelectTrigger>
                      <SelectContent><SelectItem value="none">-</SelectItem><SelectItem value="client">Clients</SelectItem><SelectItem value="prospect">Prospects</SelectItem></SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Recherche</Label>
                    <Input value={form.filter_search} onChange={e => setForm({ ...form, filter_search: e.target.value })} placeholder="Nom, société..." data-testid="campaign-filter-search" />
                  </div>
                </div>
              </details>
            </div>

            <div className="flex gap-2 justify-end border-t pt-4">
              <Button variant="outline" onClick={() => setShowCreate(false)}>Annuler</Button>
              <Button variant="outline" onClick={handleCreate} data-testid="campaign-save-draft"><Mail size={14} className="mr-1.5" />Brouillon</Button>
              <Button onClick={handleCreateAndSend} disabled={sending} data-testid="campaign-send-btn"><Send size={14} className="mr-1.5" />{sending ? "Envoi..." : "Envoyer"}</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Detail dialog */}
      <Dialog open={!!showDetail} onOpenChange={v => { if (!v) setShowDetail(null); }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          {showDetail && (<>
            <DialogHeader><DialogTitle>{showDetail.name || "Campagne"}</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                <div><span className="text-slate-500">Objet :</span> <span className="font-medium">{showDetail.subject}</span></div>
                <div><span className="text-slate-500">Statut :</span> <Badge className={`ml-1 text-[10px] ${(STATUS_MAP[showDetail.status] || STATUS_MAP.draft).class}`}>{(STATUS_MAP[showDetail.status] || STATUS_MAP.draft).label}</Badge></div>
                <div><span className="text-slate-500">Destinataires :</span> {showDetail.total_recipients || 0}</div>
                <div><span className="text-slate-500">Envoyes :</span> {showDetail.sent_count || 0} {showDetail.error_count > 0 && <span className="text-red-500">({showDetail.error_count} erreur(s))</span>}</div>
                {showDetail.list_ids?.length > 0 && <div className="col-span-2"><span className="text-slate-500">Listes :</span> {getListNames(showDetail.list_ids)}</div>}
              </div>
              <div><Label className="text-xs text-slate-500">Message</Label><div className="bg-slate-50 border rounded-md p-3 text-sm whitespace-pre-line">{showDetail.message}</div></div>
              {showDetail.details?.length > 0 && (
                <div>
                  <Label className="text-xs text-slate-500">Detail des envois ({showDetail.details.length})</Label>
                  <div className="border rounded-lg max-h-[30vh] overflow-y-auto mt-1">
                    <Table>
                      <TableHeader><TableRow className="bg-slate-50"><TableHead>Nom</TableHead><TableHead>Email</TableHead><TableHead className="text-center">Statut</TableHead></TableRow></TableHeader>
                      <TableBody>
                        {showDetail.details.map((d, i) => (
                          <TableRow key={d.email || `recipient-${i}`}>
                            <TableCell className="text-sm">{d.name}</TableCell>
                            <TableCell className="text-xs text-slate-500">{d.email}</TableCell>
                            <TableCell className="text-center">
                              {d.status === "sent" && <Badge className="text-[10px] bg-emerald-100 text-emerald-700">Envoyé</Badge>}
                              {d.status === "error" && <Badge className="text-[10px] bg-red-100 text-red-700" title={d.error}>Erreur</Badge>}
                              {d.status === "skipped" && <Badge className="text-[10px] bg-slate-100 text-slate-600" title={d.error}>Ignore</Badge>}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </div>
              )}
            </div>
          </>)}
        </DialogContent>
      </Dialog>
    </div>
  );
}
